// Lo PURO de "el agente edita un lanzamiento" —
// ADR-2026-09-09-el-agente-edita-lanzamientos.
//
// Tres cosas viven acá, y las tres son las que deciden si el usuario termina con los datos
// que quería o con otros:
//
//  1. El RESUMEN que lee el modelo: qué productos hay, con qué datos y qué les falta.
//  2. Los CAMBIOS: a qué productos aplican y qué valor queda. Un cambio de precio masivo
//     es una OPERACIÓN (×1.10, +500, redondear), no un valor suelto — si no, el modelo
//     tendría que calcular cuarenta precios de memoria y errarle a alguno.
//  3. El DIFF: de qué valor a qué valor, por producto. Es lo que se muestra ANTES de
//     escribir, porque esto no es generar una imagen: un precio mal puesto no tiene
//     ctrl+z y termina publicado en la tienda.
//
// Y la lectura de una PLANILLA: qué columna es cada campo, para que el usuario no tenga
// que renombrar nada en su Excel.

import type { ProductoDeLanzamiento } from './lanzamientosApi';

// ── Campos editables ────────────────────────────────────────────────────────

/** Los campos de la ficha que el agente puede tocar. Deliberadamente NO están los de
 *  estado (`estado`, `error`, `accion`): esos los maneja la publicación. */
export const CAMPOS_EDITABLES = [
  'nombre',
  'descripcion',
  'precio',
  'costo',
  'stock',
  'sku',
  'codigoBarras',
  'marca',
  'tags',
  'peso',
  'alto',
  'ancho',
  'profundidad',
  'seoTitulo',
  'seoDescripcion',
] as const;

export type CampoEditable = (typeof CAMPOS_EDITABLES)[number];

/** Los que son plata o medida: la operación numérica sólo tiene sentido acá. */
export const CAMPOS_NUMERICOS: CampoEditable[] = [
  'precio',
  'costo',
  'stock',
  'peso',
  'alto',
  'ancho',
  'profundidad',
];

export const esCampoEditable = (v: unknown): v is CampoEditable =>
  typeof v === 'string' && (CAMPOS_EDITABLES as readonly string[]).includes(v);

// ── Operaciones numéricas ───────────────────────────────────────────────────

export type TipoOperacion = 'fijar' | 'multiplicar' | 'sumar' | 'redondear';

export interface Operacion {
  tipo: TipoOperacion;
  valor: number;
}

const texto = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';

/** Un número de la ficha (que viaja como texto). `null` si no es un número. */
export function numeroDe(v: string | null | undefined): number | null {
  const t = (v ?? '').toString().trim().replace(/\s/g, '');
  if (!t) return null;
  // "1.999,50" (formato local) y "1999.50" (formato máquina) tienen que dar lo mismo: sin
  // esto, un "×1.10" sobre un precio con puntos de miles devuelve cualquier cosa.
  const normal = /,\d{1,2}$/.test(t) ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  const n = Number(normal);
  return Number.isFinite(n) ? n : null;
}

/**
 * Aplica la operación sobre el valor actual. `null` = no se puede (el campo estaba vacío
 * y la operación necesitaba una base): quien llama lo reporta en vez de escribir un cero.
 *
 * `redondear` con valor 990 lleva 4321 a 4990: es "terminar en", que es como se piensan
 * los precios acá, y no el redondeo a múltiplo.
 */
export function aplicarOperacion(actual: string | null, op: Operacion): string | null {
  if (op.tipo === 'fijar') return String(op.valor);
  const base = numeroDe(actual);
  if (base === null) return null;
  if (op.tipo === 'multiplicar') return String(Math.round(base * op.valor * 100) / 100);
  if (op.tipo === 'sumar') return String(Math.round((base + op.valor) * 100) / 100);
  // Terminar en N: se sube al siguiente número que termine así.
  const terminacion = Math.max(0, Math.round(op.valor));
  const escala = 10 ** String(terminacion).length;
  const piso = Math.floor(base / escala) * escala + terminacion;
  return String(piso >= base ? piso : piso + escala);
}

export function normalizarOperacion(entrada: unknown): Operacion | null {
  const o = (entrada ?? {}) as { tipo?: unknown; valor?: unknown };
  const tipo = texto(o.tipo).toLowerCase();
  const valor = Number(o.valor);
  if (!Number.isFinite(valor)) return null;
  if (tipo === 'multiplicar' && valor <= 0) return null;
  return ['fijar', 'multiplicar', 'sumar', 'redondear'].includes(tipo)
    ? { tipo: tipo as TipoOperacion, valor }
    : null;
}

// ── El diff ─────────────────────────────────────────────────────────────────

export interface CambioResuelto {
  lanzamientoProductoId: string;
  nombre: string;
  campo: CampoEditable;
  de: string;
  a: string;
}

export interface DiffDeEdicion {
  cambios: CambioResuelto[];
  /** Productos que la operación no pudo tocar, con el motivo. Se muestran igual: un
   *  "se saltearon 3" en silencio es la forma de que el usuario publique datos viejos. */
  salteados: Array<{ nombre: string; motivo: string }>;
}

const valorActual = (p: ProductoDeLanzamiento, campo: CampoEditable): string =>
  campo === 'stock' ? (p.stock === null ? '' : String(p.stock)) : ((p as any)[campo] ?? '');

/**
 * Qué productos toca un cambio. `product_ids` explícito manda; si no hay, aplica a TODOS
 * los del lanzamiento — que es lo que el usuario pide cuando dice "subí todos los precios".
 */
export function productosAlcanzados(
  productos: ProductoDeLanzamiento[],
  ids: unknown,
): ProductoDeLanzamiento[] {
  const lista = Array.isArray(ids) ? ids.map(texto).filter(Boolean) : [];
  if (lista.length === 0) return productos;
  const set = new Set(lista);
  return productos.filter((p) => set.has(p.id));
}

/**
 * Los cambios que escribió el modelo → el diff concreto, producto por producto.
 *
 * Un cambio que no cambia nada NO entra: sin esto, "poné stock 10" sobre 40 productos que
 * ya tienen 10 mostraría 40 líneas de ruido y escondería las 2 que sí cambian.
 */
export function resolverCambios(
  productos: ProductoDeLanzamiento[],
  entrada: unknown,
): DiffDeEdicion {
  const crudos = Array.isArray(entrada) ? entrada : [];
  const cambios: CambioResuelto[] = [];
  const salteados: Array<{ nombre: string; motivo: string }> = [];
  for (const c of crudos) {
    if (!c || typeof c !== 'object') continue;
    const co = c as { campo?: unknown; valor?: unknown; operacion?: unknown; product_ids?: unknown };
    const campo = texto(co.campo);
    if (!esCampoEditable(campo)) continue;
    const op = normalizarOperacion(co.operacion);
    const alcanzados = productosAlcanzados(productos, co.product_ids);
    for (const p of alcanzados) {
      const de = valorActual(p, campo);
      let a: string | null;
      if (op) {
        if (!CAMPOS_NUMERICOS.includes(campo)) {
          salteados.push({ nombre: p.nombre, motivo: `"${campo}" no es un número` });
          continue;
        }
        a = aplicarOperacion(de || null, op);
        if (a === null) {
          salteados.push({ nombre: p.nombre, motivo: `no tenía ${campo} para calcular` });
          continue;
        }
      } else {
        a = texto(co.valor);
      }
      if (a === de) continue;
      cambios.push({ lanzamientoProductoId: p.id, nombre: p.nombre, campo, de, a });
    }
  }
  return { cambios, salteados };
}

/** Los cambios agrupados por campo, para que la card muestre "Precio · 12 productos" en
 *  vez de doce líneas sueltas. */
export function agruparPorCampo(
  cambios: CambioResuelto[],
): Array<{ campo: CampoEditable; items: CambioResuelto[] }> {
  const mapa = new Map<CampoEditable, CambioResuelto[]>();
  for (const c of cambios) mapa.set(c.campo, [...(mapa.get(c.campo) ?? []), c]);
  return [...mapa.entries()].map(([campo, items]) => ({ campo, items }));
}

// ── El resumen que lee el modelo ────────────────────────────────────────────

const vacio = (v: string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : v);

/**
 * El lanzamiento, en texto, para que el modelo sepa sobre qué está trabajando. Incluye el
 * `id` de cada fila porque es lo que después manda en `product_ids`.
 */
export function resumenDeLanzamiento(args: {
  nombre: string;
  estado: string;
  productos: ProductoDeLanzamiento[];
  nombreDeCategoria?: (id: string) => string;
  /** A dónde va. El modelo tiene que saberlo: las reglas de la ficha cambian con el destino. */
  canal?: string;
}): string {
  const { nombre, estado, productos } = args;
  const destino = args.canal ? ` para ${args.canal}` : '';
  const nombreCat = args.nombreDeCategoria ?? ((id: string) => id);
  if (productos.length === 0) {
    return `Lanzamiento "${nombre}"${destino} (${estado}): todavía no tiene productos.`;
  }
  const lineas = productos.map((p, i) => {
    const cats = p.categorias.map(nombreCat).join(', ');
    const partes = [
      `#${i + 1} id=${p.id} · ${p.nombre || '(sin nombre)'}`,
      `precio ${vacio(p.precio)}`,
      `stock ${p.stock === null ? '—' : p.stock}`,
      `sku ${vacio(p.sku)}`,
      `${p.imagenes.length} foto(s)`,
      cats ? `categorías: ${cats}` : 'sin categoría',
      p.variantes.length > 0 ? `${p.variantes.length} variante(s)` : '',
      // Lo más importante de la fila: si tiene producto detrás o está esperando uno.
      p.productoId ? '' : 'BORRADOR (sin producto del catálogo: espera al que tenga ese SKU)',
    ].filter(Boolean);
    return partes.join(' · ');
  });
  return [
    `Lanzamiento "${nombre}"${destino} (${estado}), ${productos.length} producto(s). El \`id\` de cada línea es el que se usa en product_ids.`,
    ...lineas,
  ].join('\n');
}

// ── Cuál lanzamiento ────────────────────────────────────────────────────────

/**
 * El lanzamiento que el usuario tiene ABIERTO, sacado de la ruta.
 *
 * Es el camino más honesto que hay: "el lanzamiento que estoy mirando" es literalmente el
 * de la URL. La alternativa —que cada pantalla le pase el contexto al chat— acopla el chat
 * a cada pantalla para averiguar algo que ya está en la barra de direcciones.
 *
 * Importa porque el fallback es "el borrador más reciente": con dos borradores abiertos,
 * editar el equivocado significa cuarenta fichas para revertir a mano.
 */
export function idDeLanzamientoEnRuta(pathname: string | null | undefined): string | null {
  const m = /^\/lanzamientos\/([0-9a-f-]{36})(?:\/|$)/i.exec((pathname ?? '').trim());
  return m ? m[1]! : null;
}

// ── Leer una planilla ───────────────────────────────────────────────────────

/** Cómo se puede llamar cada columna en el Excel del usuario. Todo en minúscula y sin
 *  acentos: el match se hace normalizado. */
const NOMBRES_DE_COLUMNA: Record<CampoEditable, string[]> = {
  nombre: ['nombre', 'producto', 'titulo', 'title', 'name', 'descripcion corta'],
  descripcion: ['descripcion', 'description', 'detalle', 'descripcion larga'],
  precio: ['precio', 'price', 'precio venta', 'precio de venta', 'pvp', 'precio web'],
  costo: ['costo', 'cost', 'precio costo', 'costo unitario'],
  stock: ['stock', 'cantidad', 'qty', 'quantity', 'existencia', 'inventario'],
  sku: ['sku', 'codigo', 'codigo interno', 'cod', 'referencia', 'ref'],
  codigoBarras: ['codigo de barras', 'codigo barras', 'barcode', 'ean', 'ean13', 'upc', 'gtin'],
  marca: ['marca', 'brand'],
  tags: ['tags', 'etiquetas', 'palabras clave'],
  peso: ['peso', 'weight', 'kg', 'peso kg'],
  alto: ['alto', 'altura', 'height'],
  ancho: ['ancho', 'width'],
  profundidad: ['profundidad', 'largo', 'depth', 'length'],
  seoTitulo: ['seo titulo', 'titulo seo', 'meta titulo', 'seo title'],
  seoDescripcion: ['seo descripcion', 'descripcion seo', 'meta descripcion', 'seo description'],
};

const normalizar = (s: string): string =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Encabezados → qué columna es cada campo. Match exacto primero y recién después por
 * "empieza con": sin ese orden, "precio" se llevaría la columna "precio costo" si venía
 * antes, y el costo terminaría publicado como precio de venta.
 */
export function columnasDePlanilla(encabezados: string[]): Partial<Record<CampoEditable, number>> {
  const limpios = encabezados.map(normalizar);
  const out: Partial<Record<CampoEditable, number>> = {};
  const tomadas = new Set<number>();
  for (const exacto of [true, false]) {
    for (const campo of CAMPOS_EDITABLES) {
      if (out[campo] !== undefined) continue;
      const alias = NOMBRES_DE_COLUMNA[campo];
      const i = limpios.findIndex(
        (h, idx) =>
          !tomadas.has(idx) &&
          h !== '' &&
          (exacto ? alias.includes(h) : alias.some((a) => h.startsWith(a) || a.startsWith(h))),
      );
      if (i >= 0) {
        out[campo] = i;
        tomadas.add(i);
      }
    }
  }
  return out;
}

export interface FilaDePlanilla {
  campos: Partial<Record<CampoEditable, string>>;
}

/**
 * Filas de la planilla → productos por crear. La primera fila son los encabezados.
 *
 * Una fila sin nombre NI sku se descarta: es una fila vacía o un total al pie, y crearla
 * dejaría un producto fantasma en el lanzamiento.
 */
export function filasDePlanilla(filas: string[][]): {
  productos: FilaDePlanilla[];
  columnas: Partial<Record<CampoEditable, number>>;
  descartadas: number;
} {
  const columnas = columnasDePlanilla(filas[0] ?? []);
  const productos: FilaDePlanilla[] = [];
  let descartadas = 0;
  for (const fila of filas.slice(1)) {
    const campos: Partial<Record<CampoEditable, string>> = {};
    for (const [campo, i] of Object.entries(columnas)) {
      const v = texto(fila[i as number]);
      if (v) campos[campo as CampoEditable] = v;
    }
    if (!campos.nombre && !campos.sku) {
      if (fila.some((c) => texto(c))) descartadas += 1;
      continue;
    }
    productos.push({ campos });
  }
  return { productos, columnas, descartadas };
}

/** Lo que vuelve al agente después de aplicar. */
export function resumenDeEdicionParaAgente(d: DiffDeEdicion): string {
  if (d.cambios.length === 0 && d.salteados.length === 0) {
    return 'No había nada que cambiar: los valores ya estaban así.';
  }
  const porCampo = agruparPorCampo(d.cambios)
    .map((g) => `${g.campo}: ${g.items.length}`)
    .join(', ');
  const salteados = d.salteados.length > 0 ? ` Salteados: ${d.salteados.length}.` : '';
  return `${d.cambios.length} cambio(s) sobre el lanzamiento (${porCampo}).${salteados}`;
}
